import React, { useState, useRef, useEffect, useContext, createContext } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

// Styled Components
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTriggerButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.foreground};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: ${props => props.theme.muted};
    border-color: ${props => props.theme.primary};
  }

  &:focus-visible {
    outline: none !important;
    border: 1px solid ${props => props.theme.border} !important;
  }

  &:active {
    outline: none !important;
    border: 1px solid ${props => props.theme.border} !important;
  }

  &:focus {
    outline: none !important;
    border: 1px solid ${props => props.theme.border} !important;
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const DropdownContentEl = styled.div<{ $isOpen: boolean; $align?: 'start' | 'center' | 'end'; $side?: 'top' | 'bottom' }>`
  position: absolute;
  ${props => {
    if (props.$side === 'top') {
      return 'bottom: calc(100% + 4px);';
    }
    return 'top: calc(100% + 4px);';
  }}
  ${props => {
    if (props.$align === 'start') return 'left: 0;';
    if (props.$align === 'end') return 'right: 0;';
    return 'left: 50%;';
  }}
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 180px;
  max-width: 220px;
  z-index: 1100;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => {
    const translateX = props.$align === 'center' ? 'translateX(-50%)' : '';
    const translateY = props.$isOpen ? 'translateY(0)' : (props.$side === 'top' ? 'translateY(8px)' : 'translateY(-8px)');
    return [translateX, translateY].filter(Boolean).join(' ') || 'none';
  }};
  transition: all 0.15s ease;
  overflow: hidden;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  
  /* Ensure dropdown doesn't go off-screen */
  @media (max-width: 768px) {
    ${props => {
      // On small screens, if side is 'top', always open upward
      if (props.$side === 'top') {
        return 'bottom: calc(100% + 4px); top: auto;';
      }
      return '';
    }}
    ${props => props.$align === 'end' ? 'right: 0;' : 'left: 0;'}
    min-width: 160px;
    max-width: 200px;
  }
`;

const DropdownItemEl = styled.div<{ $variant?: 'default' | 'destructive'; $disabled?: boolean }>`
  padding: 12px 16px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${props => {
    if (props.$disabled) return props.theme.mutedForeground;
    return props.$variant === 'destructive' ? 'var(--destructive)' : props.theme.foreground;
  }};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};

  &:hover {
    background: ${props => {
      if (props.$disabled) return 'transparent';
      return props.$variant === 'destructive' ? 'var(--destructive)10' : props.theme.muted;
    }};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownSeparator = styled.div`
  height: 1px;
  background: ${props => props.theme.border};
  margin: 4px 0;
`;

// Context
interface DropdownCtx {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}
const DropdownContext = createContext<DropdownCtx | null>(null);

// Types
interface DropdownMenuProps {
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
  variant?: 'default' | 'destructive';
  className?: string;
  disabled?: boolean;
}

// Main DropdownMenu Component
export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggle = () => setIsOpen(prev => !prev);
  const close = () => setIsOpen(false);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <DropdownContext.Provider value={{ isOpen, toggle, close, containerRef }}>
      <DropdownContainer ref={containerRef}>{children}</DropdownContainer>
    </DropdownContext.Provider>
  );
};

// DropdownMenuTrigger Component
export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ children, asChild }) => {
  const ctx = useContext(DropdownContext);
  if (!ctx) return <>{children}</>; // safety

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      'data-dropdown-trigger': true,
      'aria-expanded': ctx.isOpen,
      type: 'button', // Prevent form submission
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        children.props?.onClick?.(e);
        ctx.toggle();
      }
    });
  }

  return (
    <DropdownTriggerButton
      $isOpen={ctx.isOpen}
      data-dropdown-trigger
      aria-expanded={ctx.isOpen}
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        ctx.toggle();
      }}
    >
      {children}
      <ChevronDown size={14} />
    </DropdownTriggerButton>
  );
};

// DropdownMenuContent Component
export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({ children, align = 'start', side = 'bottom' }) => {
  const ctx = useContext(DropdownContext);
  if (!ctx) return null;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <DropdownContentEl 
      $isOpen={ctx.isOpen} 
      $align={align} 
      $side={side}
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </DropdownContentEl>
  );
};

// DropdownMenuItem Component
export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ 
  children, 
  onClick, 
  variant = 'default',
  className,
  disabled = false
}) => {
  const ctx = useContext(DropdownContext);
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onClick?.(e);
    ctx?.close();
  };

  return (
    <DropdownItemEl 
      $variant={variant} 
      $disabled={disabled}
      onClick={handleClick}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={className}
      data-dropdown-item
    >
      {children}
    </DropdownItemEl>
  );
};

// DropdownMenuSeparator Component
export const DropdownMenuSeparator: React.FC = () => {
  return <DropdownSeparator />;
};
